import clsx from 'clsx'
import { MouseoverTooltip } from 'components/Tooltip'
import { useUSDValue } from 'hooks/useUSDValue'
import { IoMdInformationCircle as InfoIcon } from 'react-icons/io'
import { RiTokenSwapFill as SwapIcon } from 'react-icons/ri'
import { TiPlus as AddIcon } from 'react-icons/ti'
import { Link } from 'react-router-dom'
import { AnalyticsVersion } from 'types/analytics'
import { formatPercentChange } from 'utils/analytics/formatPercentChange'
import { formatAmount, formatDollarAmount } from 'utils/formatDollarAmt'
import { formatPercentage } from 'utils/numbers'

import { SkeletonBar } from '../Skeletons'

type PoolStatisticsProps = {
  version: AnalyticsVersion
  token0: {
    address: string
    symbol?: string
    balance?: number
    price?: number
  }
  token1: {
    address: string
    symbol?: string
    balance?: number
    price?: number
  }
  feeTier: number
  tvl: {
    value?: number
    percentageChange?: number
  }
  volume: {
    value?: number
    percentageChange?: number
  }
  fees: {
    value?: number
  }
  APR?: number
}

export default function PoolStatistics({
  version,
  token0,
  token1,
  feeTier,
  tvl,
  volume,
  fees,
  APR,
}: PoolStatisticsProps) {
  const token0USDValue = useUSDValue(token0.balance?.toString(), token0.price)
  const token1USDValue = useUSDValue(token1.balance?.toString(), token1.price)

  const getTradeLink = () => {
    return `/swap?inputCurrency=${token0.address}&outputCurrency=${token1.address}`
  }

  const getAddLiquidityLink = () => {
    if (version === 'v2') {
      return `/liquidity/v2/add/${token0.address}/${token1.address}`
    }

    return `/liquidity/v3/add/${token0.address}/${token1.address}/${feeTier}`
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-between items-center gap-3 w-full fixed bottom-0 left-0 right-0 z-50 p-4 bg-kodiak-bg-surface border-kodiak-border lg:relative lg:border-t-0 lg:p-0 lg:bg-transparent">
        <Link
          to={getTradeLink()}
          className="cursor-pointer flex-1 flex items-center justify-center bg-kodiak-button-primary !text-white font-medium p-2 rounded-lg hover:bg-kodiak-brand-secondary transition-colors duration-200 disabled:bg-gray-600 disabled:text-gray-400 disabled:cursor-not-allowed !shrink-0"
        >
          <SwapIcon size={18} className="mr-2" />
          Trade
        </Link>
        <Link
          to={getAddLiquidityLink()}
          className="cursor-pointer flex-1 flex items-center justify-center bg-kodiak-button-secondary !text-white font-medium p-2 rounded-lg hover:bg-kodiak-button-subtle transition-colors duration-200 disabled:bg-gray-600 disabled:text-gray-400 disabled:cursor-not-allowed !shrink-0"
        >
          <AddIcon size={18} className="mr-2" />
          Add Liquidity
        </Link>
      </div>

      <div className=" min-h-max w-full rounded-lg bg-kodiak-bg-floating flex flex-col gap-8 py-5 px-4">
        <span className="text-2xl text-kodiak-text-primary font-medium">Stats</span>

        <div className="flex flex-col gap-8">
          <div className="flex flex-row gap-4 justify-between flex-wrap items-start">
            <div className="flex flex-col gap-2 whitespace-nowrap">
              <span className="text-md text-kodiak-text-secondary">TVL</span>

              <div className="flex gap-2 items-center min-h-[2.5rem]">
                {tvl.value !== undefined ? (
                  <span className="text-3xl text-kodiak-text-primary font-medium">{formatDollarAmount(tvl.value)}</span>
                ) : (
                  <SkeletonBar className="w-32 h-8" />
                )}

                {tvl.percentageChange !== undefined && (
                  <span className={clsx('text-md font-medium', formatPercentChange(tvl.percentageChange)?.className)}>
                    {formatPercentChange(tvl.percentageChange)?.text}
                  </span>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-2  whitespace-nowrap">
              <div className="flex gap-2 items-center">
                <span className="text-md text-kodiak-text-secondary">24H Fees</span>
                <MouseoverTooltip text="Fees paid by traders in the last 24 hours" placement="top">
                  <InfoIcon />
                </MouseoverTooltip>
              </div>

              <div className="flex gap-2 items-center min-h-[2.5rem]">
                {fees.value !== undefined ? (
                  <span className="text-3xl text-kodiak-text-primary font-medium">
                    {formatDollarAmount(fees.value)}
                  </span>
                ) : (
                  <SkeletonBar className="w-22 h-8" />
                )}
              </div>
            </div>

            <div className="flex flex-col gap-2 whitespace-nowrap">
              <span className="text-md text-kodiak-text-secondary">24H Volume</span>

              <div className="flex gap-2 items-center min-h-[2.5rem]">
                {volume.value !== undefined ? (
                  <span className="text-3xl text-kodiak-text-primary font-medium">
                    {formatDollarAmount(volume.value)}
                  </span>
                ) : (
                  <SkeletonBar className="w-32 h-8" />
                )}

                {volume.percentageChange !== undefined && (
                  <span
                    className={clsx('text-md font-medium', formatPercentChange(volume.percentageChange)?.className)}
                  >
                    {formatPercentChange(volume.percentageChange)?.text}
                  </span>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-2 whitespace-nowrap">
              <div className="flex gap-2 items-center self-end sm:self-start xl:self-end">
                <span className="text-md text-kodiak-text-secondary">APR</span>
                <MouseoverTooltip text={`Annualized based on "Fees 24H"`} placement="top">
                  <InfoIcon />
                </MouseoverTooltip>
              </div>

              <div className="flex gap-2 items-center min-h-[2.5rem]">
                {APR !== undefined ? (
                  <span className="text-3xl text-kodiak-text-primary font-medium">
                    {formatPercentage(APR.toString())}
                  </span>
                ) : (
                  <SkeletonBar className="w-20 h-8" />
                )}
              </div>
            </div>

            <div className="flex flex-col gap-2 whitespace-nowrap w-full mt-2">
              <span className="text-md text-kodiak-text-secondary">Pool balances</span>
              {/* Balances header */}
              <div className="flex items-center justify-between text-kodiak-text-primary">
                {[token0, token1].map((token, i) =>
                  !token.symbol || !token.balance ? (
                    <SkeletonBar key={i} className="w-20 h-8" />
                  ) : (
                    <div key={i} className="flex items-baseline gap-1">
                      <span className="text-2xl font-semibold">{formatAmount(token.balance ?? 0)}</span>
                      {token.symbol ? <span className="text-sm text-kodiak-text-secondary">{token.symbol}</span> : null}
                    </div>
                  )
                )}
              </div>

              {/* Range bar */}
              <div
                className="w-full h-3 rounded-full bg-kodiak-card-tertiary overflow-hidden"
                aria-label="Pool token balance ratio"
              >
                {(() => {
                  // Compute USD-proportional split.
                  const aUsd = Number(token0USDValue) ?? 0
                  const bUsd = Number(token1USDValue) ?? 0

                  const totalUsd = aUsd + bUsd
                  const aPct = totalUsd > 0 ? (aUsd / totalUsd) * 100 : 50
                  const bPct = 100 - aPct

                  return (
                    <div className="w-full h-full flex gap-0.5">
                      <div className="h-full bg-kodiak-brand-light" style={{ width: `${aPct}%` }} />
                      <div className="h-full bg-kodiak-brand-secondary" style={{ width: `${bPct}%` }} />
                    </div>
                  )
                })()}
              </div>

              <div className="flex items-center justify-between text-lg font-medium text-kodiak-text-primary">
                {[token0USDValue, token1USDValue].map((item, i) => (
                  <span key={i}>
                    {item !== undefined ? `~${formatDollarAmount(item)}` : <SkeletonBar className="w-16 h-7" />}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
